/**
 * Browser side of passkeys (#43): converts the server's JSON options to what
 * `navigator.credentials` expects, and the results back to JSON.
 */
const fromB64u = (s: string) => {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};
const toB64u = (buf: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

export const passkeysSupported = () => typeof window !== "undefined" && Boolean(window.PublicKeyCredential);

interface CredentialDescriptorJson {
  type: "public-key";
  id: string;
}

export async function createPasskey(options: {
  challenge: string;
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  excludeCredentials: CredentialDescriptorJson[];
  authenticatorSelection: AuthenticatorSelectionCriteria;
  attestation: AttestationConveyancePreference;
  timeout: number;
}) {
  const cred = (await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: fromB64u(options.challenge),
      user: { ...options.user, id: fromB64u(options.user.id) },
      excludeCredentials: options.excludeCredentials.map((c) => ({ ...c, id: fromB64u(c.id) })),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("No passkey was created");
  const response = cred.response as AuthenticatorAttestationResponse;
  return {
    response: {
      clientDataJSON: toB64u(response.clientDataJSON),
      attestationObject: toB64u(response.attestationObject),
    },
  };
}

export async function getPasskeyAssertion(options: {
  challenge: string;
  rpId: string;
  allowCredentials: CredentialDescriptorJson[];
  userVerification: UserVerificationRequirement;
  timeout: number;
}) {
  const cred = (await navigator.credentials.get({
    publicKey: {
      ...options,
      challenge: fromB64u(options.challenge),
      allowCredentials: options.allowCredentials.map((c) => ({ ...c, id: fromB64u(c.id) })),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("No passkey was used");
  const response = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    response: {
      clientDataJSON: toB64u(response.clientDataJSON),
      authenticatorData: toB64u(response.authenticatorData),
      signature: toB64u(response.signature),
    },
  };
}
